import React, { useState, useEffect } from 'react';
import { Icon } from '../Common/Icons';
import { useParams, useNavigate } from 'react-router-dom';
import { pmApplicationAPI, membershipSettingsAPI } from '../../../services/membershipApi';
import { Card, Input, Button, FormField } from '../Common/MembershipUI';
import { useDetailBack } from '../../../hooks/useDetailBack';

const ScoreCardItem = ({ title, description, maxPoints, fieldName, formData, setFormData, fileLabel = 'Evidence', existingFileUrl = null }) => {
  const handlePointChange = (e) => {
    setFormData(prev => ({ ...prev, [`${fieldName}_points`]: e.target.value }));
  };
  const handleBreakupChange = (e) => {
    setFormData(prev => ({ ...prev, [`${fieldName}_breakup`]: e.target.value }));
  };
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData(prev => ({ ...prev, [fieldName]: e.target.files[0] }));
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50/55 hover:border-gray-300 transition-colors">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-sm font-medium text-gray-900">{title}</h4>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
        <div className="text-sm font-medium text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100/50">
          Max: {maxPoints} pts
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <FormField label="Points Claimed">
          <Input 
            type="number" 
            min="0" 
            max={maxPoints} 
            value={formData[`${fieldName}_points`] || ''} 
            onChange={handlePointChange} 
            placeholder="0"
          />
        </FormField>
        <FormField label="Points Break Up">
          <Input 
            type="text" 
            value={formData[`${fieldName}_breakup`] || ''} 
            onChange={handleBreakupChange} 
            placeholder="Details"
          />
        </FormField>
        <FormField label={fileLabel}>
          {formData[fieldName] ? (
            <div className="flex items-center gap-2 mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white"><Icon name="check" className="w-3 h-3" strokeWidth={3} /></span>
              <span className="text-xs text-emerald-800 font-medium truncate max-w-[140px]" title={formData[fieldName].name}>
                {formData[fieldName].name}
              </span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, [fieldName]: null }))}
                className="text-xs text-rose-600 hover:text-rose-800 underline font-medium ml-auto"
              >
                Clear
              </button>
            </div>
          ) : existingFileUrl ? (
            <div className="flex items-center gap-2 mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white"><Icon name="check" className="w-3 h-3" strokeWidth={3} /></span>
              <span className="text-xs text-emerald-800 font-medium">
                Evidence Uploaded
              </span>
              <a
                href={existingFileUrl.startsWith('http') ? existingFileUrl : `${import.meta.env.VITE_APP_API_URL || 'http://localhost:3001/api'}/membership${existingFileUrl}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-700 hover:text-blue-900 underline font-medium ml-auto"
              >
                View
              </a>
            </div>
          ) : (
            <input 
              type="file" 
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 file:text-xs file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50 hover:file:text-gray-900 mt-1 cursor-pointer"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
            />
          )}
        </FormField>
      </div>
    </div>
  );
};

const PublicPMApplicationForm = ({ isAdminView = false }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const handleBack = useDetailBack('/membership/practicing-marketer');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [application, setApplication] = useState(null);
  const [page, setPage] = useState(1); // 1 = Registration Form, 2 = Score Card
  const [scoreCardTemplate, setScoreCardTemplate] = useState([]);

  const [formData, setFormData] = useState({
    // Registration Details
    membershipNumber: '',
    fullName: '',
    nameWithInitials: '',
    designation: '',
    companyNameAndAddress: '',
    contactNumbers: '',
    email: '',
    marketingQualifications: '',
    dateAwarded: '',
    signatureAccepted: false,
  });

  useEffect(() => {
    fetchApplication();
  }, [token]);

  const fetchApplication = async () => {
    try {
      const res = await pmApplicationAPI.getPublic(token);
      if (res.data.success) {
        const appData = res.data.data;
        setApplication(appData);
        
        // Member object
        const member = appData.memberId || {};
        
        // Autofill logic
        const autoMembershipNumber = appData.membershipNumber || member.membershipId || member.manualMemberId || '';
        const autoFullName = appData.fullName || member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || '';
        const autoNameWithInitials = appData.nameWithInitials || member.nameWithInitials || `${member.initials || ''} ${member.lastName || ''}`.trim() || '';
        
        const autoDesignation = appData.designation || member.professionalInfo?.designation || member.designation || '';
        
        // Construct company name and address
        let autoCompanyAddress = appData.companyNameAndAddress || '';
        if (!autoCompanyAddress) {
          const employer = member.professionalInfo?.employer || member.employer || '';
          const addressParts = [];
          if (employer) addressParts.push(employer);
          if (member.address) {
            if (member.address.line1) addressParts.push(member.address.line1);
            if (member.address.line2) addressParts.push(member.address.line2);
            if (member.address.line3) addressParts.push(member.address.line3);
            if (member.address.city) addressParts.push(member.address.city);
          }
          autoCompanyAddress = addressParts.join(', ');
        }
        
        const autoContactNumbers = appData.contactNumbers || member.mobilePhone || member.phone || '';
        const autoEmail = appData.email || member.email || '';

        const initialScorecard = {};
        if (appData.scoreCard) {
          Object.entries(appData.scoreCard).forEach(([field, val]) => {
            if (val) {
              initialScorecard[`${field}_points`] = val.claimedPoints !== undefined ? val.claimedPoints : '';
              initialScorecard[`${field}_breakup`] = val.pointsBreakup || '';
            }
          });
        }

        setFormData(prev => ({
          ...prev,
          membershipNumber: autoMembershipNumber,
          fullName: autoFullName,
          nameWithInitials: autoNameWithInitials,
          designation: autoDesignation,
          companyNameAndAddress: autoCompanyAddress,
          contactNumbers: autoContactNumbers,
          email: autoEmail,
          marketingQualifications: appData.marketingQualifications || '',
          dateAwarded: appData.dateAwarded ? appData.dateAwarded.split('T')[0] : '',
          signatureAccepted: appData.signatureAccepted || false,
          ...initialScorecard
        }));
      }
      
      const settingsRes = await membershipSettingsAPI.getAll();
      if (settingsRes.data?.data?.pmScoreCardTemplate) {
        setScoreCardTemplate(settingsRes.data.data.pmScoreCardTemplate);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired application link.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.signatureAccepted) {
      alert("Please accept the signature declaration before submitting.");
      return;
    }
    setSubmitting(true);
    
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });
      
      const res = await pmApplicationAPI.submitPublic(token, data);
      if (res.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full p-8 text-center border-t-4 border-red-500 shadow-xl rounded-xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">Link Invalid or Expired</h2>
          <p className="text-gray-500">{error}</p>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full p-8 text-center border-t-4 border-green-500 shadow-xl rounded-xl">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-medium text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-500 mb-6">
            {isAdminView 
              ? 'The application has been successfully filled and submitted on behalf of the member.' 
              : 'Your application for Practicing Marketer [SL] has been successfully submitted. We will review your submission and contact you shortly.'}
          </p>
          {isAdminView && (
            <Button onClick={() => handleBack()} className="bg-blue-800 hover:bg-blue-900 text-white w-full">
              Return to Application List
            </Button>
          )}
        </Card>
      </div>
    );
  }

  const defaultScoreCards = [
    { fieldId: 'professionalGrowth', label: '1. Professional Growth', description: 'A certificate of achievement or a letter from your employer and an outline of the project(s) where appropriate. Promotions: Middle Manager to Senior Manager - 8 points, Senior Manager to General Manager or above - 16 points.', maxPoints: 25 },
    { fieldId: 'studiesRelated', label: '2. Studies related to Qualifications', description: 'A copy of your qualification certificate or a letter of attendance signed by your tutor', maxPoints: 25 },
    { fieldId: 'trainingCourses', label: '3. Brief training courses and work shops', description: 'A certificate of attendance or a signed letter of attendance from your course tutor.', maxPoints: 25 },
    { fieldId: 'sharingKnowledge', label: '4. Sharing knowledge', description: 'Copies of books, papers, journals or articles.', maxPoints: 25 },
    { fieldId: 'mentoring', label: '5. Mentoring', description: 'A letter from the individual being mentored or from the educational establishment.', maxPoints: 15 },
    { fieldId: 'conferences', label: '6. Conferences and exhibitions', description: 'Your booking/confirmation letter or name badge.', maxPoints: 7 },
    { fieldId: 'privateStudy', label: '7. Private study', description: 'Where applicable, evidenced by noting the source, date and documenting the relevance of the information obtained.', maxPoints: 6 },
    { fieldId: 'contributionToProfession', label: '8. Contribution to Profession', description: 'A copy of the minutes or a letter/email confirmation from the organizers.', maxPoints: 6 },
    { fieldId: 'professionalEvents', label: '9. Professional events', description: 'A certificate of attendance or a copy of agenda', maxPoints: 15 },
  ];
  
  const scoreCards = scoreCardTemplate.length > 0 ? scoreCardTemplate : defaultScoreCards;

  return (
    <div className={`min-h-screen bg-gray-50 ${isAdminView ? 'py-4 px-2' : 'py-8 px-4 sm:px-6 lg:px-8'}`}>
      <div className="max-w-4xl mx-auto">
        {isAdminView ? (
          <div className="mb-6">
            <button 
              type="button" 
              onClick={() => handleBack()}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors mb-2"
            >
              Back to PM Applications
            </button>
            <h1 className="text-2xl font-medium text-gray-900">Practicing Marketer Application Form</h1>
            <p className="text-sm text-gray-500">Fill out and submit on behalf of {formData.fullName || 'member'}</p>
          </div>
        ) : (
          <div className="text-center mb-8">
            <img src="/logo.png" alt="SLIM Logo" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-medium text-gray-900">Sri Lanka Institute of Marketing</h1>
            <h2 className="text-xl font-medium text-blue-800 mt-2">PRACTICING MARKETER [SL] APPLICATION</h2>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="flex border-b border-gray-200 bg-gray-50">
            <button 
              type="button"
              className={`flex-1 py-4 text-sm font-medium text-center ${page === 1 ? 'bg-blue-800 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setPage(1)}
            >
              1. Registration Form
            </button>
            <button 
              type="button"
              className={`flex-1 py-4 text-sm font-medium text-center ${page === 2 ? 'bg-blue-800 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setPage(2)}
            >
              2. Score Card
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            {page === 1 ? (
              <div className="space-y-6">
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mb-6">
                  <h3 className="text-lg font-medium text-blue-900 mb-1">Registration Form</h3>
                  <p className="text-sm text-blue-800 font-medium">Please verify your details and fill out all required fields.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField label="Membership Number" required>
                    <Input value={formData.membershipNumber} onChange={e => setFormData({...formData, membershipNumber: e.target.value})} />
                  </FormField>
                  <FormField label="Full Name" required>
                    <Input value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
                  </FormField>
                  <FormField label="Name with Initials" required>
                    <Input value={formData.nameWithInitials} onChange={e => setFormData({...formData, nameWithInitials: e.target.value})} />
                  </FormField>
                  <FormField label="Designation" required>
                    <Input value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} />
                  </FormField>
                  <FormField label="Company Name & Address" required className="md:col-span-2">
                    <Input value={formData.companyNameAndAddress} onChange={e => setFormData({...formData, companyNameAndAddress: e.target.value})} />
                  </FormField>
                  <FormField label="Contact Numbers" required>
                    <Input value={formData.contactNumbers} onChange={e => setFormData({...formData, contactNumbers: e.target.value})} />
                  </FormField>
                  <FormField label="E Mail" required>
                    <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </FormField>
                  <FormField label="Marketing Qualifications" required>
                    <Input value={formData.marketingQualifications} onChange={e => setFormData({...formData, marketingQualifications: e.target.value})} />
                  </FormField>
                  <FormField label="Date Awarded">
                    <Input type="date" value={formData.dateAwarded} onChange={e => setFormData({...formData, dateAwarded: e.target.value})} />
                  </FormField>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <Button type="button" onClick={() => setPage(2)} size="lg" className="bg-blue-800 hover:bg-blue-900 text-white font-medium">
                    Next: Score Card
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mb-6">
                  <h3 className="text-lg font-medium text-blue-900 mb-1">Score Card Assessment</h3>
                  <p className="text-sm text-blue-800 font-medium">Please provide points and upload evidence for each applicable activity category below.</p>
                </div>
                
                {scoreCards.map((card, idx) => (
                  <ScoreCardItem 
                    key={idx}
                    title={card.label}
                    description={card.description}
                    maxPoints={card.maxPoints}
                    fieldName={card.fieldId}
                    formData={formData}
                    setFormData={setFormData}
                    existingFileUrl={application?.scoreCard?.[card.fieldId]?.evidenceUrl}
                  />
                ))}

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="signature"
                        type="checkbox"
                        checked={formData.signatureAccepted}
                        onChange={(e) => setFormData({...formData, signatureAccepted: e.target.checked})}
                        className="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500 cursor-pointer"
                        required
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="signature" className="font-medium text-gray-900 cursor-pointer select-none">Declaration & Signature</label>
                      <p className="text-gray-500 mt-0.5">I declare that the information provided in this application is true and correct to the best of my knowledge.</p>
                      <p className="text-gray-500 text-xs mt-1">Date: {new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200">
                  <Button type="button" onClick={() => setPage(1)} variant="outline" size="lg" className="font-medium">
                    Back to Registration Form
                  </Button>
                  <Button type="submit" size="lg" isLoading={submitting} disabled={!formData.signatureAccepted} className="bg-blue-800 hover:bg-blue-900 text-white font-medium">
                    Submit Application
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicPMApplicationForm;
